import { useState, type FormEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import KeyIcon from '@mui/icons-material/Key'
import { Loading } from './Loading'
import { LoginForm } from './LoginForm'
import { LoginDialogLayout, type LoginDialogLayoutProps } from './LoginDialogLayout'

export { nimbraEdgeLogo, nimbraEdgeOsLogo, nimbraConsoleLogo } from './LoginDialogLayout'

const MAX_LENGTH = 255

const commonInputProps = {
    variant: 'outlined' as const,
    margin: 'normal' as const,
    required: true,
    fullWidth: true,
    slotProps: {
        htmlInput: { maxLength: MAX_LENGTH },
        inputLabel: { shrink: true },
    },
} as const satisfies TextFieldProps

/** Explains why typing stopped once an input reaches the maxLength limit; the value itself is valid */
const maxLengthProps = (value: string): Pick<TextFieldProps, 'helperText'> =>
    value.length >= MAX_LENGTH ? { helperText: `Maximum ${MAX_LENGTH} characters` } : {}

export interface SsoLoginMethod {
    strategy: string
    displayName: string
}

export interface InlineMfaPrompt {
    error?: string
    onCredentialsChanged?: () => void
    onPasskeyLogin?: () => void
}

export interface LoginDialogProps extends Omit<LoginDialogLayoutProps, 'children' | 'title'> {
    /** Called with credentials when the user submits the form */
    onLogin: (credentials: { username: string; password: string; otp?: string }) => void
    /** Whether a login request is in progress */
    loading?: boolean
    /** Error message to display (e.g. "Invalid credentials") */
    error?: string
    /** Whether MFA input should be shown instead of username/password */
    mfaRequired?: boolean
    /** TOTP prompt revealed below credentials after a successful password check */
    inlineMfa?: InlineMfaPrompt
    /** SSO login methods to display below the form */
    ssoLoginMethods?: SsoLoginMethod[]
    /** Called when an SSO button is clicked */
    onSsoLogin?: (strategy: string) => void
    /** Called when the passkey sign-in button is clicked */
    onPasskeyLogin?: () => void
    /** Title text (default: "Sign in") */
    title?: string
}

export const LoginDialog = ({
    onLogin,
    loading,
    error,
    mfaRequired,
    inlineMfa,
    ssoLoginMethods,
    onSsoLogin,
    onPasskeyLogin,
    logo,
    logoSrc,
    title = 'Sign in',
}: LoginDialogProps) => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [otp, setOtp] = useState('')

    const alternateLoginAvailable = !mfaRequired && ((ssoLoginMethods ?? []).length > 0 || !!onPasskeyLogin)

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        let submittedOtp: string | undefined
        if (mfaRequired || inlineMfa) submittedOtp = otp
        onLogin({ username, password, otp: submittedOtp })
        setOtp('')
    }

    const updateUsername = (value: string) => {
        setUsername(value)
        if (!inlineMfa) return
        setOtp('')
        inlineMfa.onCredentialsChanged?.()
    }

    const updatePassword = (value: string) => {
        setPassword(value)
        if (!inlineMfa) return
        setOtp('')
        inlineMfa.onCredentialsChanged?.()
    }

    let submitDisabled = !username || !password
    if (inlineMfa) {
        submitDisabled = submitDisabled || !otp
    }
    if (mfaRequired) submitDisabled = submitDisabled || !otp

    return (
        <LoginDialogLayout logo={logo} logoSrc={logoSrc} title={title}>
            <Loading loading={loading} fullScreen />
            {alternateLoginAvailable && (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3, width: '100%' }}>
                        {onPasskeyLogin && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<KeyIcon />}
                                onClick={onPasskeyLogin}
                                disabled={loading}
                                fullWidth
                            >
                                Sign in with passkey
                            </Button>
                        )}
                        {(ssoLoginMethods ?? []).map((method) => (
                            <Button
                                data-testid={`sso-${method.strategy}`}
                                key={method.strategy}
                                variant="outlined"
                                color="secondary"
                                onClick={() => onSsoLogin?.(method.strategy)}
                                fullWidth
                            >
                                {method.displayName}
                            </Button>
                        ))}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: 3,
                            width: '80%',
                        }}
                    >
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.500' }} />
                        <Typography variant="body1" sx={{ marginX: 2 }}>
                            or sign in with
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.500' }} />
                    </Box>
                </>
            )}
            <LoginForm
                onSubmit={handleSubmit}
                submitDisabled={submitDisabled}
                error={!mfaRequired ? error : undefined}
                usernameProps={{
                    ...maxLengthProps(username),
                    value: username,
                    onChange: (event) => updateUsername(event.target.value),
                    slotProps: commonInputProps.slotProps,
                }}
                passwordProps={{
                    ...maxLengthProps(password),
                    value: password,
                    onChange: (event) => updatePassword(event.target.value),
                    slotProps: commonInputProps.slotProps,
                }}
                fields={
                    mfaRequired ? (
                        <TextField
                            {...commonInputProps}
                            {...maxLengthProps(otp)}
                            value={otp}
                            onChange={(event) => setOtp(event.target.value)}
                            id="mfacode"
                            label="MFA code"
                            autoComplete="one-time-code"
                            autoFocus
                        />
                    ) : undefined
                }
            >
                {!mfaRequired && (
                    <Collapse in={!!inlineMfa} timeout={250} unmountOnExit>
                        <TextField
                            autoFocus
                            required
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={otp}
                            onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                            id="totp-code"
                            label="MFA code"
                            autoComplete="one-time-code"
                            error={!!inlineMfa?.error}
                            helperText={inlineMfa?.error}
                            slotProps={{
                                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 },
                                inputLabel: { shrink: true },
                            }}
                        />
                        {inlineMfa?.onPasskeyLogin && (
                            <Button fullWidth onClick={inlineMfa.onPasskeyLogin} disabled={loading}>
                                Use a passkey instead
                            </Button>
                        )}
                    </Collapse>
                )}
            </LoginForm>
        </LoginDialogLayout>
    )
}
